import { getFirmwareApiGateway } from 'common';

import {
  isFirmwareOverrideEnabled,
  getFirmwareOverrides,
  createMockAsyncState,
  shouldOverrideEndpoint,
  type FirmwareOverrides,
} from '../../utils/firmwareConfig';
import { HttpClient } from '../../utils/httpClient';
import { useAsyncState } from '../../utils/useAsyncState';

export interface WifiNetworksResponse {
  networks: Array<{
    ssid: string;
    signal: number;
    secure: boolean;
  }>;
}

export interface WifiStatusApiResponse {
  status: {
    interface: string;
    ip: string;
    ssid: string;
    status:
      | 'activated'
      | 'deactivating'
      | 'disconnected'
      | 'unavailable'
      | 'unknown'
      | 'unmanaged'
      | 'connected'
      | 'preparing'
      | 'failed'
      | 'need-auth';
  };
}

export interface WifiStatusResponse {
  interface: string;
  ip: string;
  ssid: string;
  status:
    | 'activated'
    | 'deactivating'
    | 'disconnected'
    | 'unavailable'
    | 'unknown'
    | 'unmanaged'
    | 'connected'
    | 'preparing'
    | 'failed'
    | 'need-auth';
}

export interface WifiConnectResponse {
  status: string;
  ssid: string;
}

export interface WifiDisconnectResponse {
  status: string;
}

export interface WifiSaveResponse {
  status: string;
  ssid: string;
}

export interface WifiForgetResponse {
  status: string;
  ssid: string;
}

interface UseWifiManagementOptions {
  isOverride?: boolean;
  customOverrides?: FirmwareOverrides;
}

export function useWifiManagement(options?: UseWifiManagementOptions) {
  const httpClient = new HttpClient(getFirmwareApiGateway());

  // Check if build-time override is enabled or manual override is requested
  const isOverrideMode = options?.isOverride || isFirmwareOverrideEnabled();
  const overrides = options?.customOverrides || getFirmwareOverrides();

  // WiFi Networks - use override or API call
  const networks =
    isOverrideMode && shouldOverrideEndpoint('/wifi/networks') && overrides.wifiNetworks
      ? createMockAsyncState(overrides.wifiNetworks)
      : useAsyncState(() => httpClient.get<WifiNetworksResponse>('/wifi/networks'));

  // WiFi Status - use override or API call
  const status =
    isOverrideMode && shouldOverrideEndpoint('/wifi/status') && overrides.wifiStatus
      ? createMockAsyncState(overrides.wifiStatus)
      : useAsyncState(() => httpClient.get<WifiStatusApiResponse>('/wifi/status'));

  const refreshData = async () => {
    const promises: Promise<void>[] = [];

    // Only execute API calls for endpoints that are not overridden
    if (!isOverrideMode || !shouldOverrideEndpoint('/wifi/networks')) {
      promises.push(networks.execute());
    }
    if (!isOverrideMode || !shouldOverrideEndpoint('/wifi/status')) {
      promises.push(status.execute());
    }

    await Promise.all(promises);
  };

  const connect = async (ssid: string, password: string): Promise<WifiConnectResponse> => {
    if (isOverrideMode && shouldOverrideEndpoint('/wifi/connect')) {
      // In override mode, simulate the API call without actually making it
      console.log(`🔧 [Override] Would connect to WiFi: ${ssid}`);
      return { status: 'connected', ssid };
    }

    return httpClient.post<WifiConnectResponse>('/wifi/connect', { ssid, password });
  };

  const disconnect = async (): Promise<WifiDisconnectResponse> => {
    if (isOverrideMode && shouldOverrideEndpoint('/wifi/disconnect')) {
      // In override mode, simulate the API call without actually making it
      console.log('🔧 [Override] Would disconnect from WiFi');
      return { status: 'disconnected' };
    }

    return httpClient.post<WifiDisconnectResponse>('/wifi/disconnect');
  };

  const save = async (ssid: string, password: string): Promise<WifiSaveResponse> => {
    if (isOverrideMode && shouldOverrideEndpoint('/wifi/save')) {
      // In override mode, simulate the API call without actually making it
      console.log(`🔧 [Override] Would save WiFi credentials for: ${ssid}`);
      return { status: 'saved', ssid };
    }

    return httpClient.post<WifiSaveResponse>('/wifi/save', { ssid, password });
  };

  const forget = async (ssid: string): Promise<WifiForgetResponse> => {
    if (isOverrideMode && shouldOverrideEndpoint('/wifi/forget')) {
      // In override mode, simulate the API call without actually making it
      console.log(`🔧 [Override] Would forget WiFi network: ${ssid}`);
      return { status: 'forgotten', ssid };
    }

    return httpClient.post<WifiForgetResponse>('/wifi/forget', { ssid });
  };

  return {
    networks,
    status,
    refreshData,
    connect,
    disconnect,
    save,
    forget,
  };
}
